import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, InfoNote } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { PlugZap, ArrowRight, ArrowLeft, HelpCircle, Layers, Server, ChevronRight } from "lucide-react";

function ContractCard({ c }) {
  return (
    <Card className="p-4" data-testid={`integration-${c.name.toLowerCase().split(" ")[0]}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-sm font-bold text-slate-900"><PlugZap className="h-4 w-4 text-slate-400" /> {c.name}</div>
        <span className="rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700">{c.status}</span>
      </div>
      <div className="mb-3 text-xs text-slate-500">Vendor / system: <span className="font-medium text-slate-700">{c.vendor}</span></div>
      {(c.known?.length > 0 || c.unknown?.length > 0) && (
        <div className="mb-3 grid gap-2 sm:grid-cols-2">
          {c.known?.length > 0 && (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2" data-testid={`known-${c.name.toLowerCase().split(" ")[0]}`}>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Known</div>
              <ul className="space-y-0.5">{c.known.map((k) => <li key={k} className="text-[11px] text-emerald-900">• {k}</li>)}</ul>
            </div>
          )}
          {c.unknown?.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2" data-testid={`unknown-${c.name.toLowerCase().split(" ")[0]}`}>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800">Unknown · To Be Confirmed</div>
              <ul className="space-y-0.5">{c.unknown.map((u) => <li key={u} className="text-[11px] text-amber-900">• {u}</li>)}</ul>
            </div>
          )}
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {c.inbound?.length > 0 && (
          <div>
            <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"><ArrowLeft className="h-3 w-3" /> Expected inbound</div>
            <div className="flex flex-wrap gap-1">{c.inbound.map((x) => <span key={x} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{x}</span>)}</div>
          </div>
        )}
        {c.outbound?.length > 0 && (
          <div>
            <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"><ArrowRight className="h-3 w-3" /> Expected outbound</div>
            <div className="flex flex-wrap gap-1">{c.outbound.map((x) => <span key={x} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">{x}</span>)}</div>
          </div>
        )}
      </div>
      <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2.5">
        <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-800"><HelpCircle className="h-3 w-3" /> Integration questions (To Be Confirmed)</div>
        <ul className="space-y-0.5">{c.questions.map((q) => <li key={q} className="text-xs text-amber-900">• {q}</li>)}</ul>
      </div>
    </Card>
  );
}

function AdapterCard({ a }) {
  return (
    <Card className="p-4" data-testid={`adapter-${a.interface.toLowerCase()}`}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-sm font-bold text-slate-900"><Layers className="h-4 w-4 text-slate-400" /> {a.role}</div>
        <span className="rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700">{a.status}</span>
      </div>
      <div className="mb-2 font-mono text-[10px] text-slate-400">{a.interface}</div>

      {a.current?.length > 0 && (
        <div className="mb-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Current implementation</div>
          {a.current.map((v) => (
            <div key={v.vendor} className="rounded-md border border-sky-200 bg-sky-50 p-2" data-testid={`adapter-current-${v.vendor.toLowerCase().split(" ")[0]}`}>
              <div className="text-xs font-semibold text-sky-800">{v.vendor}</div>
              <div className="text-[11px] text-sky-900/80">{v.note}</div>
            </div>
          ))}
        </div>
      )}

      {a.prospective?.length > 0 && (
        <div className="mb-2">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Prospective / future implementation</div>
          {a.prospective.map((v) => (
            <div key={v.vendor} className="mb-1 rounded-md border border-dashed border-slate-300 bg-slate-50 p-2">
              <div className="text-xs font-semibold text-slate-700">{v.vendor}</div>
              <div className="text-[11px] text-slate-500">{v.note}</div>
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Neutral contract capabilities</div>
        <div className="flex flex-wrap gap-1">{a.capabilities.map((cap) => <span key={cap} className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">{cap}()</span>)}</div>
      </div>
    </Card>
  );
}

export default function Integrations() {
  const [contracts, setContracts] = useState(null);
  const [future, setFuture] = useState([]);
  const [arch, setArch] = useState(null);
  useEffect(() => {
    api.integrationContracts().then(setContracts).catch(() => {});
    api.futureTestAreas().then(setFuture).catch(() => {});
    api.integrationArchitecture().then(setArch).catch(() => {});
  }, []);

  const flow = arch?.adapters
    ? [arch.adapters[0]?.role, "RequiSure Core (vendor-neutral)", ...arch.adapters.slice(1).map((a) => a.role)]
    : [];

  return (
    <div>
      <PageHeader testid="integrations-header" title="Integrations"
        subtitle="Vendor identities are partly confirmed (AdvanceMD · CrelioHealth · INVOPAD · PayMedics · Novitas JH · BCBS), but every external platform remains NOT CONNECTED. No live connectivity is simulated." />
      <InfoNote className="mb-4">RequiSure does not integrate real EMR, LIS, RCM, or payer systems in this prototype. Knowing a vendor's identity is not the same as a technical connection — each card shows what is Known versus what is still To Be Confirmed.</InfoNote>

      {/* Vendor-Neutral Integration Architecture */}
      <Card className="mb-6 p-4" data-testid="integration-architecture">
        <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Layers className="h-4 w-4 text-slate-400" /> Vendor-Neutral Integration Architecture</div>
        <p className="mb-3 text-xs text-slate-500">{arch?.note || "RequiSure Core talks to neutral adapter contracts. Named vendors are prospective or current implementations of those contracts — never hard-coded into RequiSure Core."}</p>

        {flow.length > 0 && (
          <div className="mb-4 flex items-stretch gap-1 overflow-x-auto" data-testid="architecture-flow">
            {flow.map((label, i) => {
              const core = label.startsWith("RequiSure");
              return (
                <React.Fragment key={label + i}>
                  <div className={`flex min-w-[150px] flex-1 flex-col justify-center rounded-md border p-2.5 ${core ? "border-teal-300 bg-teal-50/50" : "border-slate-200 bg-white"}`}>
                    {core ? <Server className="mb-1 h-4 w-4 text-teal-600" /> : <Layers className="mb-1 h-4 w-4 text-slate-400" />}
                    <span className="text-xs font-bold text-slate-900">{label}</span>
                    {!core && <span className="mt-1 inline-flex w-fit rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-rose-700">Not Connected</span>}
                  </div>
                  {i < flow.length - 1 && <div className="flex items-center"><ChevronRight className="h-4 w-4 text-slate-300" /></div>}
                </React.Fragment>
              );
            })}
          </div>
        )}

        <div className="grid gap-3 lg:grid-cols-2">
          {arch?.adapters?.map((a) => <AdapterCard key={a.interface} a={a} />)}
        </div>
      </Card>

      <div className="mb-2 text-sm font-semibold text-slate-800">Vendor Integration Contracts (identity known · not connected)</div>
      <div className="grid gap-3 lg:grid-cols-3">
        {contracts && ["advancemd", "lis", "rcm", "novitas", "bcbs"].map((k) => contracts[k] && <ContractCard key={k} c={contracts[k]} />)}
      </div>

      <Card className="mt-4 p-4">
        <div className="mb-2 text-sm font-semibold text-slate-800">Future Test Areas (backlog only — not built)</div>
        <p className="mb-3 text-xs text-slate-500">RPP remains the sole functional demonstration category in this prototype.</p>
        <div className="flex flex-wrap gap-2">
          {future.map((f) => (
            <span key={f.name} className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-1.5 text-xs text-slate-600" data-testid={`future-test-${f.name.split(" ")[0].toLowerCase()}`}>
              {f.name} <span className="text-[10px] text-slate-400">· {f.note}</span>
            </span>
          ))}
        </div>
      </Card>
    </div>
  );
}
